import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, BookOpen, AlertTriangle, CheckCircle, Timer, Play } from 'lucide-react';
import { Test } from '../types/test';
import { testService } from '../services/testService';
import { formatWithTimezone, getSecondsDiff, debugTimestamp } from '../utils/dateUtils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const TestInstructionsPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeUntilStart, setTimeUntilStart] = useState<number>(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (testId) {
      loadTest();
    }
  }, [testId]);

  useEffect(() => {
    if (test) {
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [test]);

  const loadTest = async () => {
    try {
      if (!testId) return;
      const testData = await testService.getTestById(testId);
      setTest(testData);
    } catch (error) {
      console.error('Failed to load test:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTimer = () => {
    if (!test) return;

    // Use getSecondsDiff for accurate countdown
    const seconds = getSecondsDiff(test.scheduledStartTime);
    
    // Debug timestamp in development
    if (process.env.NODE_ENV === 'development') {
      debugTimestamp(test.scheduledStartTime, 'Test Scheduled Start Time');
    }

    setTimeUntilStart(Math.max(0, seconds));
  };

  const handleStartTest = async () => {
    if (!test) return;

    // Frontend guard to prevent early start regardless of backend flag
    const now = new Date();
    const allowedTime = new Date(test.allowedStartTime || test.scheduledStartTime);
    if (now < allowedTime) {
      alert('This test is not yet available. Please wait until the scheduled start time.');
      return;
    }

    try {
      // Check if test has expired before starting
      const timerSync = await testService.syncTimer(test.id);
      if (timerSync.isExpired || timerSync.timeRemaining <= 0) {
        // Check if this is due to tenant policy (force completion)
        if (timerSync.forceCompletion && timerSync.message) {
          // Show tenant-specific message
          alert(timerSync.message);
          navigate('/');
          return;
        }

        // Check if this is a scheduled test that requires force submission
        if (timerSync.forceSubmission) {
          // Show alert for scheduled test expiration
          alert(timerSync.message || 'Scheduled test time has ended. You cannot start this test.');
          navigate('/');
          return;
        }

        // Create expired test result and redirect to results
        try {
          const { testResultService } = await import('../services/testResultService');
          const expiredResult = await testResultService.createExpiredTestResult(test.id);
          navigate(`/results/${expiredResult.id}`);
        } catch (error) {
          console.error('Failed to create expired test result:', error);
          navigate(`/results/${test.id}`);
        }
        return;
      }

      // If a session exists, resume it; otherwise start fresh when allowed
      const existing = await testService.resumeTest(test.id);
      if (existing) {
        navigate(`/test/${test.id}/take`);
        return;
      }

      // Double-check server side readiness via canStartTest flag
      if (!test.canStartTest) {
        alert('This test is not yet available to start. Please try again closer to the start time.');
        return;
      }

      await testService.startTest(test.id);
      navigate(`/test/${test.id}/take`);
    } catch (error: any) {
      console.error('Failed to start test:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      if (errorMessage.includes('expired') || errorMessage.includes('time')) {
        // Create expired test result and redirect to results
        try {
          const { testResultService } = await import('../services/testResultService');
          const expiredResult = await testResultService.createExpiredTestResult(test.id);
          navigate(`/results/${expiredResult.id}`);
        } catch (expiredError) {
          console.error('Failed to create expired test result:', expiredError);
          navigate(`/results/${test.id}`);
        }
      } else {
        alert(`Failed to start test: ${errorMessage}`);
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-3 py-4 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="container mx-auto px-3 py-4 min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Test Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The requested test could not be found.
            </p>
            <Button onClick={() => navigate('/')}>
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 py-4 max-w-4xl">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{test.title}</CardTitle>
          <div className="text-muted-foreground">{test.subject}</div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Test Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <Clock className="h-6 w-6 mx-auto mb-2 text-blue-600" />
              <div className="text-sm font-medium">Duration</div>
              <div className="text-lg font-semibold">{test.duration} minutes</div>
            </div>
            <div className="text-center">
              <BookOpen className="h-6 w-6 mx-auto mb-2 text-green-600" />
              <div className="text-sm font-medium">Questions</div>
              <div className="text-lg font-semibold">{test.totalQuestions}</div>
            </div>
            <div className="text-center">
              <Timer className="h-6 w-6 mx-auto mb-2 text-[#0456FF]" />
              <div className="text-sm font-medium">Scheduled Start</div>
              <div className="text-lg font-semibold">
                {formatWithTimezone(test.scheduledStartTime)}
              </div>
            </div>
          </div>

          {/* Timer */}
          {!test.canStartTest && (
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold mb-2">Test starts in:</h3>
              <div className="text-3xl font-bold text-blue-600">
                {formatTime(timeUntilStart)}
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Please wait until the scheduled start time
              </div>
            </div>
          )}

          {test.canStartTest && (
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-green-800">Ready to Start!</h3>
              <div className="text-sm text-green-700">
                You can now begin your test
              </div>
            </div>
          )}

          {/* Critical Warning */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center text-red-800">
              <AlertTriangle className="h-6 w-6 mr-2 text-red-600" />
              CRITICAL WARNING
            </h3>
            <div className="space-y-2 text-red-700">
              <div className="font-semibold">
                • If you EXIT this test after starting, it will be marked as COMPLETED
              </div>
              <div className="font-semibold">
                • You CANNOT resume the test once you leave
              </div>
              <div className="font-semibold">
                • Ensure stable internet connection before starting
              </div>
              <div className="font-semibold">
                • Do not close browser or navigate away during the test
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
              Test Instructions
            </h3>
            <div className="space-y-3">
              {test.instructions.map((instruction, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-semibold text-blue-600">{index + 1}</span>
                  </div>
                  <span className="text-sm">{instruction}</span>
                </div>
              ))}
            </div>
          </div>

          {/* General Guidelines */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">General Guidelines:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Do not refresh the page or navigate away during the test</li>
              <li>• Your progress is automatically saved</li>
              <li>• You can mark questions for review and return to them later</li>
              <li>• Submit your test before the time expires</li>
              <li>• Once submitted, you cannot make changes</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
            >
              Back to Dashboard
            </Button>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={!test.canStartTest}
                  className="px-8"
                >
                  {test.canStartTest ? (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Start Test
                    </>
                  ) : (
                    'Wait for Start Time'
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" aria-hidden="true" />Final Confirmation</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-3">
                      <span className="font-semibold text-gray-800 block">
                        Are you ready to start the test?
                      </span>
                      <div className="bg-red-50 border border-red-200 rounded p-3">
                        <span className="text-red-700 font-medium block">
                          <strong>WARNING:</strong> Once you start:
                        </span>
                        <ul className="text-red-600 text-sm mt-2 space-y-1">
                          <li>• Exiting will mark the test as COMPLETED</li>
                          <li>• You cannot resume if you leave</li>
                          <li>• Timer will start immediately</li>
                          <li>• Ensure stable internet connection</li>
                        </ul>
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleStartTest}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Yes, Start Test
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestInstructionsPage;
